import { useState, useMemo } from "react";
import type { Event, EventCategory } from "@/types";

interface DateRange {
  start: Date | null;
  end: Date | null;
}

interface PriceRange {
  min: number;
  max: number;
}

interface UseEventFiltersReturn {
  dateRange: DateRange;
  priceRange: PriceRange;
  selectedCategories: EventCategory[];
  setDateRange: (range: DateRange) => void;
  setPriceRange: (range: PriceRange) => void;
  setSelectedCategories: (categories: EventCategory[]) => void;
  hasActiveFilters: boolean;
  filteredEvents: (events: Event[]) => Event[];
  clearAll: () => void;
  clearCategory: () => void;
  clearPrice: () => void;
  clearDate: () => void;
}

/**
 * Custom hook to manage event filter state centrally.
 * Handles date range, price range, and category selection.
 */
export function useEventFilters(): UseEventFiltersReturn {
  const [dateRange, setDateRange] = useState<DateRange>({
    start: null,
    end: null,
  });
  const [priceRange, setPriceRange] = useState<PriceRange>({
    min: 0,
    max: 300,
  });
  const [selectedCategories, setSelectedCategories] = useState<EventCategory[]>(
    [],
  );

  const hasActiveFilters = useMemo(
    () =>
      dateRange.start !== null ||
      dateRange.end !== null ||
      priceRange.min > 0 ||
      priceRange.max < 300 ||
      selectedCategories.length > 0,
    [dateRange, priceRange, selectedCategories],
  );

  /**
   * Filter events by multiple criteria: selected categories, price range, and date range.
   * All active filters are applied conjunctively (AND logic):
   * - If category filter is set, event must match one of the selected categories
   * - Event price must be within [min, max] range
   * - If date range is set, event date must fall within that range
   * @param events - Array of events to filter
   * @returns Filtered subset of events matching all active criteria
   */
  const filteredEvents = (events: Event[]): Event[] => {
    return events.filter((event) => {
      // Filter by categories
      if (
        selectedCategories.length > 0 &&
        !selectedCategories.includes(event.category)
      ) {
        return false;
      }

      // Filter by price range — max=300 means slider is at maximum ("no upper limit").
      if (event.price < priceRange.min) return false;
      if (priceRange.max < 300 && event.price > priceRange.max) return false;

      // Filter by date range
      // Parse only the date part (YYYY-MM-DD) to avoid UTC→local timezone shift.
      // event.dateevent is stored as UTC midnight (e.g. "2026-04-15T00:00:00Z"),
      // which in Ecuador (UTC-5) would become April 14 at 7pm — wrong day.
      if ((dateRange.start || dateRange.end) && event.dateevent) {
        const datePart = event.dateevent.slice(0, 10); // "2026-04-15"
        const parts = datePart.split("-").map(Number);
        const [eYear, eMonth, eDay] = [parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0];
        const eventDateLocal = new Date(eYear, eMonth - 1, eDay);
        if (dateRange.start && eventDateLocal < dateRange.start) return false;
        if (dateRange.end && eventDateLocal > dateRange.end) return false;
      }

      return true;
    });
  };

  const clearAll = () => {
    setDateRange({ start: null, end: null });
    setPriceRange({ min: 0, max: 300 });
    setSelectedCategories([]);
  };

  const clearCategory = () => {
    setSelectedCategories([]);
  };

  const clearPrice = () => {
    setPriceRange({ min: 0, max: 300 });
  };

  const clearDate = () => {
    setDateRange({ start: null, end: null });
  };

  return {
    dateRange,
    priceRange,
    selectedCategories,
    setDateRange,
    setPriceRange,
    setSelectedCategories,
    hasActiveFilters,
    filteredEvents,
    clearAll,
    clearCategory,
    clearPrice,
    clearDate,
  };
}
